
import React, { useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Globe } from "lucide-react";
import { format } from "date-fns";
import { formatTimeSlot } from "@/lib/time-utils";
import { Helmet } from "react-helmet-async";

const BookingConfirmedPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  
  const meetingSlug = searchParams.get('meeting');
  const dateParam = searchParams.get('date');
  const timeParam = searchParams.get('time');
  const timezoneParam = searchParams.get('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const calendarStatus = searchParams.get('calendar') || 'pending';
  const bookingId = searchParams.get('bookingId');
  
  useEffect(() => {
    if (!meetingSlug || !dateParam || !timeParam) {
      navigate('/');
    }
  }, [meetingSlug, dateParam, timeParam, navigate]);

  if (!meetingSlug || !dateParam || !timeParam) {
    return null;
  }
  
  const date = new Date(dateParam);
  const formattedDate = format(date, "EEEE, MMMM d, yyyy");

  const formatTimeZone = (tz: string) => {
    try {
      const now = new Date();
      const tzName = new Intl.DateTimeFormat('en', {
        timeZoneName: 'short',
        timeZone: tz
      }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';
      
      return `${tz.replace('_', ' ')} (${tzName})`;
    } catch (e) {
      return tz;
    }
  };

  // Format time using the correct timezone
  const formattedTime = timeParam.includes('AM') || timeParam.includes('PM') 
    ? timeParam 
    : formatTimeSlot(timeParam, timezoneParam);

  // Generate page title and description for SEO
  const pageTitle = "Booking Confirmed | ChronoCal";
  const pageDescription = `Your meeting has been successfully scheduled for ${formattedDate} at ${formattedTime}.`;
  
  // Schema.org JSON-LD structured data for Event
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Scheduled Meeting",
    "startDate": `${dateParam}T${timeParam}`,
    "location": {
      "@type": "VirtualLocation",
      "name": "Online Meeting"
    },
    "organizer": {
      "@type": "Organization",
      "name": "ChronoCal"
    }
  };

  return (
    <div className="min-h-screen bg-white py-8 sm:py-12">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="noindex" /> {/* Don't index confirmation pages */}
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        
        {/* Schema.org JSON-LD structured data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <div className="container max-w-sm sm:max-w-lg mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Booking Confirmed!</CardTitle>
            <CardDescription className="text-sm sm:text-base">
              Your meeting has been scheduled successfully
            </CardDescription>
          </CardHeader>
          
          <CardContent className="py-4 sm:py-6 space-y-3 sm:space-y-4">
            <div className="bg-green-50 text-green-700 p-3 sm:p-4 rounded-md flex items-start">
              <div className="mr-2 sm:mr-3 mt-0.5">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <div>
                <h2 className="font-medium text-sm sm:text-base">Meeting Details</h2>
                <p className="text-xs sm:text-sm mt-1">
                  {formattedDate} at {formattedTime}
                </p>
                <div className="flex items-center text-xs sm:text-sm mt-1 text-green-600">
                  <Globe className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="truncate">{formatTimeZone(timezoneParam)}</span>
                </div>
              </div>
            </div>
            
            <div className={`p-3 sm:p-4 rounded-md flex items-start ${calendarStatus === 'sent' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
              <div className="mr-2 sm:mr-3 mt-0.5">
                {calendarStatus === 'sent' ? <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5" /> : <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />}
              </div>
              <div>
                <h2 className="font-medium text-sm sm:text-base">Calendar Invitation</h2>
                <p className="text-xs sm:text-sm mt-1">
                  {calendarStatus === 'sent' 
                    ? "A calendar invitation has been sent to your email. Please check your inbox and add it to your calendar."
                    : "Your calendar invitation is being processed. You may receive an email with calendar details shortly."}
                </p>
              </div>
            </div>
            
            <div className="flex flex-col gap-2 sm:gap-3 mt-4 sm:mt-6 border-t pt-4 sm:pt-6">
              {bookingId ? (
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button variant="outline" size="sm" className="w-full sm:w-auto">
                    <Link to={`/booking/${bookingId}/cancel`} className="w-full h-full flex items-center justify-center">
                      Cancel Meeting
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" className="w-full sm:w-auto">
                    <Link to={`/booking/${bookingId}/reschedule`} className="w-full h-full flex items-center justify-center">
                      Reschedule
                    </Link>
                  </Button>
                </div>
              ) : (
                <div className="text-center text-xs sm:text-sm text-muted-foreground">
                  Meeting management options are not available
                </div>
              )}
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col space-y-2 sm:space-y-3 pt-4 sm:pt-6">
            <Button className="w-full" size="sm" asChild>
              <Link to="/">Return to Homepage</Link>
            </Button>
            
            <div className="text-center text-xs text-muted-foreground pt-2 sm:pt-3">
              <p>Scheduled with ChronoCal, the energy-aware scheduling platform</p>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default BookingConfirmedPage;
